import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  getOrganizationByIdentifier,
  getOrganizations,
  setCurrentOrganization,
} from '../redux/actions/organizationActions';
import { selectCurrentUser } from '../selectors/authSelectors';
import { getUserOrganizationsMemoized, selectOrganizationLoading } from '../selectors/organizationSelectors';

/**
 * Encapsulates all logic around resolving/choosing the current organization.
 */
export const useOrganizationSelector = () => {
  const dispatch = useDispatch();
  const userOrganizations = useSelector(getUserOrganizationsMemoized);
  const userOrganizationsFetched = useSelector(selectOrganizationLoading);
  const currentUser = useSelector(selectCurrentUser);
  const { companyName } = useParams();
  const [searchParams] = useSearchParams();
  const organizationIdentifier = companyName || searchParams.get('company');

  useEffect(() => {
    if (currentUser) {
      dispatch(getOrganizations());
    }
  }, [currentUser, dispatch]);

  useEffect(() => {
    if (currentUser && !userOrganizationsFetched) return;

    if (currentUser && userOrganizations.length) {
      // Priority 0: explicit identifier
      const orgFromIdentifier =
        organizationIdentifier &&
        userOrganizations.find(({ organization }) => organization.general_setting_identifier === organizationIdentifier)
          ?.organization;

      // Priority 1: WorkOS orgUuid
      const workosUuid = searchParams.get('orgUuid');
      const workosOrg = userOrganizations.find(({ organization }) => organization.uuid === workosUuid)?.organization;

      // Priority 2: saved “selectedOrganization” in localStorage
      const savedOrg = JSON.parse(localStorage.getItem('selectedOrganization') || 'null');

      let validSavedOrg = userOrganizations.find(
        ({ organization }) => savedOrg && organization.uuid === savedOrg.uuid
      )?.organization;

      // Case of Boon org
      if (savedOrg && savedOrg.name === 'Boon' && !validSavedOrg) {
        validSavedOrg = { id: null, name: 'Boon', logo_image_thumb: '' };
      }

      // Priority 3: first org in the list
      const fallbackOrg = userOrganizations[0]?.organization;

      const organizationToUse = orgFromIdentifier || workosOrg || validSavedOrg || fallbackOrg;

      if (organizationToUse) {
        dispatch(setCurrentOrganization(organizationToUse));
        localStorage.setItem('selectedOrganization', JSON.stringify(organizationToUse));
      }

      return;
    }

    // User has **no** organizations: fall back to a default “Boon” org
    if (currentUser && userOrganizationsFetched && userOrganizations.length === 0) {
      const defaultOrg = { id: null, name: 'Boon', logo_image_thumb: '' };
      dispatch(setCurrentOrganization(defaultOrg));
      localStorage.setItem('selectedOrganization', JSON.stringify(defaultOrg));
    }

    if (!currentUser && organizationIdentifier) {
      // Get org from identifier
      dispatch(getOrganizationByIdentifier(organizationIdentifier));
    }
  }, [userOrganizationsFetched, userOrganizations, organizationIdentifier, dispatch, currentUser]);
};
